
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

export default function WaitlistSection() {
  const [formData, setFormData] = useState({
    email: '',
    persona: '',
    interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) return;

    setIsSubmitting(true);
    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('persona', formData.persona);
      formDataToSubmit.append('interest', formData.interest);

      await fetch('https://readdy.ai/api/form/d30mc3iud5nqmcq0e2q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      setIsSubmitted(true);
      setFormData({ email: '', persona: '', interest: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const personas = [
    'Women (Hormonal Health)',
    'Athletes (Performance)',
    'Elderly (Age-Related)',
    'Metro/Altitude Residents',
    'General Health Interest'
  ];

  const interests = [
    'Personal Health Monitoring',
    'Athletic Performance',
    'Medical Professional Use',
    'Research Purposes',
    'Family Health Management'
  ];

  return (
    <section 
      className="py-20 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/4b19f6ce802b667029862d178078faf5.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-white/95"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Be First to Experience
            <br />
            <span className="text-[#12B6B6]">OxyAI™</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Join thousands of early adopters who will get exclusive access to OxyAI™ 
            when it launches. No spam, just health innovation.
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-4">Welcome to the Waitlist!</h3>
              <p className="text-lg text-gray-700 mb-8">
                You'll be among the first to know when OxyAI™ is ready. 
                We'll send you exclusive updates and early access.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="text-center">
                  <i className="ri-smartphone-line text-2xl text-[#12B6B6] mb-2"></i>
                  <p className="text-sm text-gray-600">App Download Link</p>
                </div>
                <div className="text-center">
                  <i className="ri-gift-line text-2xl text-[#12B6B6] mb-2"></i>
                  <p className="text-sm text-gray-600">Beta Testing Access</p>
                </div>
                <div className="text-center">
                  <i className="ri-vip-crown-line text-2xl text-[#12B6B6] mb-2"></i>
                  <p className="text-sm text-gray-600">Exclusive Updates</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                  <i className="ri-smartphone-line text-3xl text-[#12B6B6]"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111] mb-2">Join the Waitlist</h3>
                <p className="text-gray-600">Get notified when OxyAI™ launches and receive exclusive early access.</p>
              </div>

              <form onSubmit={handleSubmit} id="oxyai-app-waitlist" data-readdy-form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Which best describes you?</label>
                  <div className="relative">
                    <select
                      name="persona"
                      value={formData.persona}
                      onChange={(e) => setFormData({...formData, persona: e.target.value})}
                      className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm appearance-none"
                    >
                      <option value="">Select your profile</option>
                      {personas.map((persona, index) => (
                        <option key={index} value={persona}>{persona}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#111111] mb-2">Primary interest in OxyAI™</label>
                  <div className="relative">
                    <select
                      name="interest"
                      value={formData.interest}
                      onChange={(e) => setFormData({...formData, interest: e.target.value})}
                      className="w-full px-4 py-3 pr-8 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm appearance-none"
                    >
                      <option value="">Select your interest</option>
                      {interests.map((interest, index) => (
                        <option key={index} value={interest}>{interest}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Joining Waitlist...' : 'Join Waitlist'}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By joining, you agree to receive updates about OxyAI™. 
                  We respect your privacy and won't share your information.
                </p>
              </form>
            </>
          )}
        </div>

        {/* Additional Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-[#12B6B6]/10 rounded-xl flex items-center justify-center">
              <i className="ri-vip-crown-line text-xl text-[#12B6B6]"></i>
            </div>
            <h4 className="font-bold text-[#111111] mb-2">Early Access</h4>
            <p className="text-sm text-gray-600">Be among the first to use OxyAI™ before public launch.</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-[#12B6B6]/10 rounded-xl flex items-center justify-center">
              <i className="ri-discount-percent-line text-xl text-[#12B6B6]"></i>
            </div>
            <h4 className="font-bold text-[#111111] mb-2">Special Pricing</h4>
            <p className="text-sm text-gray-600">Exclusive launch pricing for waitlist members.</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-[#12B6B6]/10 rounded-xl flex items-center justify-center">
              <i className="ri-feedback-line text-xl text-[#12B6B6]"></i>
            </div>
            <h4 className="font-bold text-[#111111] mb-2">Shape the Future</h4>
            <p className="text-sm text-gray-600">Your feedback will help improve OxyAI™ for everyone.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
